import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;       // explicit canonical; auto-derived from pathname when omitted
  type?: string;
  structuredData?: object | object[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  article?: boolean;
  noindex?: boolean;
}

const BLOG_DEFAULT_OG = 'https://www.agilitytech.ai/og-blog-default.png';
const SITE_DEFAULT_OG = 'https://www.agilitytech.ai/agility hex fonts.png';

const SEOHead: React.FC<SEOHeadProps> = ({
  title = "Agility | AI Consulting & Data Analytics Solutions",
  description = "Transform your business with AI-powered solutions. Expert AI consulting, automation, data engineering, and predictive analytics. 200+ successful implementations across healthcare, manufacturing, and finance.",
  keywords = "AI consulting, artificial intelligence, machine learning, data analytics, automation, predictive analytics, data engineering, business intelligence, digital transformation, AI strategy",
  image,
  url,
  type = "website",
  structuredData,
  author = "Agility",
  publishedTime,
  modifiedTime,
  article = false,
  noindex = false,
}) => {
  const { pathname } = useLocation();
  // Strip query parameters and hash fragments, then trailing slash (except /)
  const cleanPathname = (() => {
    let p = pathname.split('?')[0].split('#')[0];
    if (p !== '/' && p.endsWith('/')) p = p.slice(0, -1);
    return p;
  })();
  const canonicalUrl = url ?? `https://www.agilitytech.ai${cleanPathname}`;
  const isBlogRoute = cleanPathname.startsWith('/insights/');
  const effectiveImage = image ?? (isBlogRoute ? BLOG_DEFAULT_OG : SITE_DEFAULT_OG);
  const schemaArray = Array.isArray(structuredData) ? structuredData : structuredData ? [structuredData] : [];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow"} />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />

      {/* Viewport and Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={article ? "article" : type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={effectiveImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Agility" />
      <meta property="og:locale" content="en_US" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={effectiveImage} />
      <meta name="twitter:site" content="@agilityai" />
      <meta name="twitter:creator" content="@agilityai" />

      {/* Additional SEO Tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta httpEquiv="x-ua-compatible" content="ie=edge" />

      {/* Canonical URL points to the www canonical for every page */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Structured Data */}
      {schemaArray.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;